import Link from "next/link";
import Image from "next/image";
import React, { useRef, useState } from "react";
import CountdownBar from "../CountdownBar";
import SocialMedia from "../SocialMedia";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import styles from "../../styles/Home.module.scss";
import { useTranslation } from "next-i18next";
import parse from "html-react-parser";
import { useRegionInfo, useNewsInfo } from "../../services/apiHooks";
import { useRouter } from "next/router";
import moment from "moment";
import WeatherWidget from "@components/weather/WeatherWidget";
import { registerRequest } from "@services/azure/authConfig";
import { useMsal } from "@azure/msal-react";
import { GoogleMap, Marker, useLoadScript, InfoWindow } from "@react-google-maps/api";
import MapMarker from '../../public/assets/svg/map-marker.svg';

const slickSettings = {
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: false,
  lazyLoad: true,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

let currentAnimation;

export default function HomePage(props) {
  const router = useRouter();
  const { instance } = useMsal();
  const { weather } = useRegionInfo(router.locale);
  const { news } = useNewsInfo(router.locale);
  const { t } = useTranslation("common");
  const [mouseMoved, setMouseMoved] = useState(false);
  const sliderRef = useRef();


  const handleLogin = (loginType) => {
    if (loginType === "popup") {
      instance.loginPopup(registerRequest).catch((e) => {
        console.log(e);
      });
    } else if (loginType === "redirect") {
      instance.loginRedirect(registerRequest).catch((e) => {
        console.log(e);
      });
    }
  };

  React.useEffect(() => {
    import("lottie-interactive/dist/lottie-interactive.js");
  });

  const onNewsClick = (link) => {
    if (!mouseMoved) window.open(link);
  };

  const weatherInfo = {
    phrase: weather?.results[0].phrase,
    iconCode: weather?.results[0].iconCode,
    temperature: Math.round(weather?.results[0].temperature.value),
    relativeHumidity: Math.round(weather?.results[0].relativeHumidity),
    wind: Math.round(weather?.results[0].wind.speed.value),
  };

  const { phrase, iconCode, temperature, relativeHumidity, wind } = weatherInfo;

  switch (iconCode) {
    case 1: /* Sunny */
    case 2 /* Mostly Sunny */:
      currentAnimation = "/assets/lottie/sun.json";
      break;

    case 3: /* Partly Sunny */
    case 4: /* Intermittent Clouds */
    case 5 /* Hazy Sunshine */:
      currentAnimation = "/assets/lottie/sun-and-cloud.json";
      break;

    case 6 /* Mostly Cloudy */:
    case 7: /* Cloudy */
    case 8: /* Dreary */
    case 11 /* Fog */:
      currentAnimation = "/assets/lottie/cloud.json";
      break;

    case 12: /* Showers */
    case 13: /* Mostly Cloudy with Showers */
    case 14: /* Partly Sunny with Showers */
    case 18 /* Rain */:
      currentAnimation = "/assets/lottie/showers.json";

    default:
      currentAnimation = "/assets/lottie/sun-and-cloud.json";
  }

  return (
    <>
      <section className={`banner banner position-relative d-flex`}>
        {/* Background Video */}
        <div className={styles.videoWrapper}>
          <video
            autoPlay
            muted
            loop
            playsInline
            className={styles.videoBg}
            poster="/assets/images/banners/home-banner.png"
          >
            <source src="https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/hero-banner-bg-video-webm.webm" type="video/webm" />
            Your browser does not support the video tag.
          </video>
          <div className="banner-overlay"></div>
        </div>

        {/* Foreground Content */}
        <div className="container foreground-content position-relative z-2 my-auto banner-text">
          <div className={`content`}>
            <div className="d-flex align-items-center mb-3">
              {/* <Link href="https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/HMP_EVENT-user-mannual.pdf">
                <a target="_blank">
                  <p className={`text-white ${styles.textUnderline}`}>{t("howcaniapply")}</p>
                  <img src="/assets/svg/left-arrow.svg" alt="arrow" width={16} height={16} />
                </a>
              </Link> */}
            </div>
            <h1 className="text-white">{t("hayyatext")}</h1>
            <p className="text-white hayya-description">{t("hayyadescription")}</p>
            <button className="banner-btn" onClick={() => handleLogin('redirect')}>
              {t("applyForFilmPermitBtn")}
            </button>
          </div>
        </div>
      </section>
      <div>
        <YourMediaFacilitator t={t} />
        <YourEventBasedGateway t={t} />
        <StepsToApply t={t} />
        <MapPlaces t={t} />
        <MediaPortalNumbers t={t} />
        <EventsSection t={t} />
      </div>
    </>
  );
}



const YourMediaFacilitator = ({ t }) => {
  return (
    <section className="position-relative z-2 my-auto media-facilitator">
      <div className="container">
        <div className="media-content-wrapper justify-content-between align-items-center">
          <div className="left-sect">
            <h1>{t("yourmediafacilitator")}</h1>
            <p>{t("yourmediafacilitator_desc")}</p>
          </div>
          <div className="right-sect">
            <img src="https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/Group-51263.png" className="sect-img" />
          </div>
        </div>
      </div>
    </section>
  )
};

const YourEventBasedGateway = ({ t }) => {
  return (
    <section className="position-relative z-2 my-auto event-based-gateway">
      <div className="container">
        <div className="row">
          {/* <div className="header">
            <h1 className="header-text">{t("youreventbasedgatewayTitle")}</h1>
            <p className="header-subtext">{t("youreventbasedgatewaySubTitle")}</p>
          </div> */}

          <div className="col-12 col-md-6 left-sect">
            <div className="header">
              <h1 className="header-text">{t("youreventbasedgatewayTitle")}</h1>
              <p className="header-subtext">{t("youreventbasedgatewaySubTitle")}</p>
            </div>
            {[{ icon: "/assets/svg/badge.svg", text: t("eventbasedgatewaystep1"), desc: t("eventbasedgatewaystep1desc") },
            { icon: "/assets/svg/shield.svg", text: t("eventbasedgatewaystep2"), desc: t("eventbasedgatewaystep2desc") },
            { icon: "/assets/svg/file.svg", text: t("eventbasedgatewaystep3"), desc: t("eventbasedgatewaystep3desc") },
            { icon: "/assets/svg/movie.svg", text: t("eventbasedgatewaystep4"), desc: t("eventbasedgatewaystep4desc") }
            ].map((item, index) => (
              <div className="item-list" key={index}>
                <div className="icon-wrapper">
                  <img src={item.icon} alt="icon" />
                </div>
                <div>
                  <h3>{item.text}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))
            }
          </div>
          <div className="col-12 col-md-6 right-sect">
            <img src="https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/homepage-2.svg" className="sect-img" />
          </div>
        </div>
      </div>
    </section>
  )
};

const StepsToApply = ({ t }) => {
  const videoRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);


  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!hasLoaded) {
      video.src =
        "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/film-permit-guide-video-webm.mp4";
      video.load(); // Ensure video loads before playing
      video.play();
      setHasLoaded(true);
      setIsPlaying(true);
    } else {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const steps = [
    {
      id: 1,
      title: t("stepstoapplystep1title"),
      description: t("stepstoapplystep1desc"),
      image: "/assets/svg/create-account-user.svg",
      alt: "Create account",
    },
    {
      id: 2,
      title: t("stepstoapplystep2title"),
      description: t("stepstoapplystep2desc"),
      image: "/assets/svg/select-event.svg",
      alt: "Complete profile",
    },
    {
      id: 3,
      title: t("stepstoapplystep3title"),
      description: t("stepstoapplystep3desc"),
      image: "/assets/svg/file-icon.svg",
      alt: "Complete profile",
    },
    {
      id: 4,
      title: t("stepstoapplystep4title"),
      description: t("stepstoapplystep4desc"),
      image: "/assets/svg/movie-icon.svg",
      alt: "Start using platform",
    },
    {
      id: 5,
      title: t("stepstoapplystep5title"),
      description: t("stepstoapplystep5desc"),
      image: "/assets/svg/shield-white.svg",
      alt: "Start using platform",
    },
  ];

  return (
    <section className="steps-to-apply">
      <div className="container">
        {/* Title */}
        {/* <h2 className="fw-bold mb-5">{t("stepstoapplytitle")}</h2> */}

        <div className="row">
          {/* Left: Video */}
          <div className="col-12 col-md-6 mb-4 mb-md-0">
            <div className="left-sect">
              <video
                ref={videoRef}
                // muted
                loop
                playsInline
                className="w-100 h-100 shadow-sm "
                poster="https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/video-thumbnail.png"
                onClick={handleVideoClick}
              >
                {/* <source
                  src="https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/film-permit-guide-video-webm.mp4"
                  type="video/webm"
                /> */}
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* Right: Steps */}
          <div className="col-12 col-md-6">

            <div className="stepper-container">
              <div className="vertical-stepper">
                <h2 className="mb-5 steps-to-apply-title">{t("stepstoapplytitle")}</h2>
                {steps.map((step, index) => {
                  return (
                    <div key={index} className="step-item">
                      <div
                        className={`step-indicator-wrapper ${index === steps.length - 1 ? 'last-step' : ''}`}
                      >
                        <div className="icon-wrapper">
                          <img src={step.image} alt={step.alt} />
                        </div>
                      </div>

                      <div className="step-content">
                        <h3 className="step-title">
                          {step.title}
                        </h3>
                        <p className="step-description">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


          </div>
        </div>
      </div>
    </section>
  );
};

const MapPlaces = ({ t }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeMarker, setActiveMarker] = useState(null); // Track which marker is clicked
  const sliderRef = useRef(null);

  // Places Data
  const places = [
    {
      id: 1,
      title: t("place1title"),
      description: t("place1desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(10).jpg",
      position: { lat: 25.361168483795126, lng: 51.524537598442 },
    },
    {
      id: 2,
      title: t("place2title"),
      description: t("place2desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(11).jpg",
      position: { lat: 25.260488755468558, lng: 51.435657114291416 },
    },
    {
      id: 3,
      title: t("place3title"),
      description: t("place3desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(13).jpg",
      position: { lat: 25.31337170551626, lng: 51.41784638203877 },
    },
    {
      id: 4,
      title: t("place4title"),
      description: t("place4desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(12).jpg",
      position: { lat: 25.371945777967195, lng: 51.55158299161492 },
    },
    {
      id: 5,
      title: t("place5title"),
      description: t("place5desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(16).jpg",
      // position: { lat: 25.361137927384448, lng: 51.52448745881659 },
    },
    {
      id: 6,
      title: t("place6title"),
      description: t("place6desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(17).jpg",
      position: { lat: 25.288464464025104, lng: 51.54927382831082 },
    },
    {
      id: 7,
      title: t("place7title"),
      description: t("place7desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/ministry-of-municipality.jpg",
      // position: { lat: 25.361137927384448, lng: 51.52448745881659 },
    },
    {
      id: 8,
      title: t("place8title"),
      description: t("place8desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(14).jpg",
      // position: { lat: 25.361137927384448, lng: 51.52448745881659 },
    },
    {
      id: 9,
      title: t("place9title"),
      description: t("place9desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(9).jpg",
      // position: { lat: 25.361137927384448, lng: 51.52448745881659 },
    },
    {
      id: 10,
      title: t("place10title"),
      description: t("place10desc"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/film-location/fl-2-(1).jpg",
      // position: { lat: 25.361137927384448, lng: 51.52448745881659 },
    }
  ];

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
  });

  if (!isLoaded) return <div>Loading map...</div>;


  return (
    <section className="map-places-section">
      <div className="container">
        <div className="row">
          <h2 className="map-heading mb-3">{t("approvedfilmlocationstitle")}</h2>
          <p className="map-description">{t("approvedfilmlocationsdesc")}</p>
          {/* LEFT: Carousel */}
          <div className="col-12 col-lg-8 position-relative map-slider">
            <Slider
              ref={sliderRef}
              arrows={false}
              infinite={false}
              slidesToShow={places?.length > 2 ? 3 : 1}
              slidesToScroll={1}
              beforeChange={(_, next) => setSelectedIndex(next)}
              responsive={
                [
                  // {
                  //   breakpoint: 1024,
                  //   settings: {
                  //     slidesToShow: places?.length > 2 ? 2 : 1,
                  //     slidesToScroll: 1,
                  //   },
                  // },
                  // {
                  //   breakpoint: 768,
                  //   settings: {
                  //     slidesToShow: 2,
                  //     slidesToScroll: 1,
                  //   },
                  // }
                  {
                    breakpoint: 1400,
                    settings: {
                      slidesToShow: 2,
                      slidesToScroll: 1,
                    }
                  },
                  {
                    breakpoint: 900,
                    settings: {
                      slidesToShow: 2,
                      slidesToScroll: 1,
                    }
                  },
                  {
                    breakpoint: 520,
                    settings: {
                      centerMode: true,
                      slidesToShow: 1,
                      slidesToScroll: 1,
                    }
                  }
                ]
              }
            >
              {places?.map((place, i) => (
                <div key={place.id} className="slider-item">
                  <div className="card">
                    <img
                      src={place.image}
                      alt={place.title}
                      className="w-100"
                    />
                    <div className="content">
                      <h5>{place.title}</h5>
                      <p className="mb-0">{place.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>

            {/* Custom Arrows */}
            <button
              className="carousel-arrow left"
              onClick={() => sliderRef.current?.slickPrev()}
              disabled={selectedIndex === 0}
            >
              {/* <img src="/assets/svg/arrow-left-black.svg" alt="arrow" width={16} height={16} /> */}
            </button>
            <button
              className="carousel-arrow right"
              onClick={() => sliderRef.current?.slickNext()}
              disabled={selectedIndex === places.length - 3}
            >
              {/* <img src="/assets/svg/arrow-right-black.svg" alt="arrow" width={16} height={16} /> */}
            </button>
          </div>

          {/* RIGHT: Map */}
          <div className="col-12 col-lg-4 col-md-12">
            <div className="map-wrapper">
              <GoogleMap
                zoom={12}
                center={places[selectedIndex]?.position}
                mapContainerStyle={{ width: "100%", height: "100%" }}
              >
                {places.map((place, i) => (
                  <Marker
                    key={place.id}
                    position={place?.position}
                    onClick={() => {
                      setSelectedIndex(i);
                      sliderRef.current?.slickGoTo(i);
                      setActiveMarker(i); // Set the clicked marker as active
                    }}
                    icon={{
                      url:
                        i === selectedIndex
                          ? "/assets/svg/map-marker.svg"
                          : "/assets/svg/map-marker.svg",
                      scaledSize: new google.maps.Size(40, 40),
                      origin: new google.maps.Point(0, 0),
                      anchor: new google.maps.Point(20, 40),
                    }}
                  />
                ))}

                {/* Show InfoWindow if a marker is clicked */}
                {activeMarker !== null && (
                  <InfoWindow
                    position={places[activeMarker].position}
                    onCloseClick={() => setActiveMarker(null)} // Close when clicking the close button
                  >
                    <div>
                      <h5>{places[activeMarker].title}</h5>
                      <img
                        src={places[activeMarker].image}
                        alt={places[activeMarker].title}
                        style={{ width: '200px', height: 'auto', borderRadius: '8px' }}
                      />
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Hayya Media Portal Numbers
const MediaPortalNumbers = ({ t }) => {
  const numbers = [
    { id: 1, value: "36", label: t("mediaportalachievement1label"), icon: "/assets/svg/calendar.svg", },
    // { id: 2, value: "10M+", label: "Accreditation Number" },
    { id: 3, value: "15,000+", label: t("mediaportalachievement2label"), icon: "/assets/svg/calendar.svg", },
    { id: 4, value: "2,700+", label: t("mediaportalachievement3label"), icon: "/assets/svg/calendar.svg", },

  ];
  return (
    <section className="media-portal-numbers">
      <div className="container">
        <div className="row justify-content-center text-center g-4">
          <h1 className="heading">{t("hayyamediaportalachievementstitle")}</h1>
          {numbers.map((num) => (
            <div key={num.id} className="col-6 col-md-4">
              <div className="number-card">
                <img src={num?.icon} alt="icon" />
                <h2 className="number-value">{num.value}</h2>
                <p className="number-label">{num.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

  )
}

const EventsSection = ({ t }) => {
  const slider2 = useRef();

  const cards = [
    {
      id: 1,
      title: t("eventhmp1title"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/events/event1.png",
      description: t("eventhmp1desc"),
      redirectLink: "https://qatar.websummit.com/?gad_source=1&gad_campaignid=22262126580"
    },
    {
      id: 2,
      title: t("eventhmp2title"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/events/event2.png",
      description: t("eventhmp2desc"),
      redirectLink: "https://www.mwcdoha.com/"
    },
    {
      id: 3,
      title: t("eventhmp3title"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/events/event3.png",
      description: t("eventhmp3desc"),
      redirectLink: "https://www.fifa.com/en/tournaments/mens/u17worldcup/qatar-2025"
    },
    {
      id: 4,
      title: t("eventhmp4title"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/events/event4.png",
      description: t("eventhmp4desc"),
      redirectLink: "https://www.fifa.com/en/tournaments/mens/intercontinentalcup/2025/articles/intercontinental-cup-dates-venues-confirmed"
    },
    {
      id: 5,
      title: t("eventhmp5title"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/events/event5.png",
      description: t("eventhmp5desc"),
      redirectLink: "https://www.formula1.com/en/racing/2025/qatar"
    },
    {
      id: 6,
      title: t("eventhmp6title"),
      image: "https://sahayyacrossingprod.blob.core.windows.net/web-assests/hmp/home-page-images/events/event6.png",
      description: t("eventhmp6desc"),
      redirectLink: "https://www.fifa.com/en/tournaments/mens/arab-cup/qatar-2025"
    },
  ];

  const SliderSettings2 = {
    dots: false,
    slidesToShow: 4,
    infinite: false,

    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        }
      },
      {
        breakpoint: 520,
        settings: {
          centerMode: true,
          centerPadding: "18%",
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ],
  }

  return (
    <section className="events-section">
      <div className="container">
        {/* Header */}
        <div>
          <h2 className="header-title">{t("eventspoweredbyHMPtitle")}</h2>
        </div>


        <div className="">
          {Array.isArray(cards) && <Slider {...SliderSettings2} className="page-slider" ref={slider2}>
            {cards?.map((card) => {
              return (
                <div key={card.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                  <div className="event-card">
                    {/* Image */}
                    <div className="event-image">
                      <img src={card.image} alt={card.title} />
                    </div>

                    {/* Overlay Content */}
                    <div className="event-content">
                      <div className="event-text">
                        <h5 className="event-title">{card.title}</h5>
                        <p className="event-desc">{card.description}</p>
                      </div>
                      <a href={card?.redirectLink} className="learn-more" target="_blank">
                        {t("learnmore")} <img src="/assets/svg/left-arrow.svg" className="ms-2" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>}
        </div>
      </div>
    </section>
  );
};
